import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Plus, MapPin, Calendar, Clock, Users, Phone, X, Trash2, Sparkles, MessagesSquare, ChevronRight } from "lucide-react";
import { toast, Toaster } from "sonner";
import { ddmmyyyy } from "../lib/dates";
import TimePicker12, { fmt12 } from "../components/TimePicker12";

const SPORTS = ["Football", "Box Cricket", "Badminton", "Basketball", "Pickleball", "Padel"];

function fmtDate(d) { return d.toISOString().split("T")[0]; }

export default function Community() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("games");
    const [posts, setPosts] = useState([]);
    const [centres, setCentres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [sportFilter, setSportFilter] = useState("All");
    const [form, setForm] = useState({
        sport: "Football", centre_id: "",
        post_date: fmtDate(new Date(Date.now() + 86400000)),
        start_time: "19:00", end_time: "20:00", players_needed: 4, price_per_share: 0, note: "",
    });
    const [groups, setGroups] = useState([]);
    const [loadingGroups, setLoadingGroups] = useState(false);
    const [showGroupForm, setShowGroupForm] = useState(false);
    const [groupForm, setGroupForm] = useState({ title: "", description: "", sport: "", image_url: "" });

    const fetchPosts = async () => {
        setLoading(true);
        const params = sportFilter !== "All" ? { sport: sportFilter } : {};
        const { data } = await api.get("/finder/posts", { params });
        setPosts(data);
        setLoading(false);
    };

    const fetchGroups = async () => {
        setLoadingGroups(true);
        const { data } = await api.get("/community/groups");
        setGroups(data);
        setLoadingGroups(false);
    };

    useEffect(() => {
        api.get("/centres").then(({ data }) => {
            setCentres(data);
            if (data.length && !form.centre_id) setForm(f => ({ ...f, centre_id: data[0].id }));
        });
    // eslint-disable-next-line
    }, []);
    useEffect(() => { fetchPosts(); /* eslint-disable-next-line */ }, [sportFilter]);
    useEffect(() => { if (tab === "groups") fetchGroups(); /* eslint-disable-next-line */ }, [tab]);

    const submit = async (e) => {
        e.preventDefault();
        // Mod 5 — validate end_time > start_time and players_needed is a positive number
        const pn = parseInt(form.players_needed);
        if (!pn || pn < 1) { toast.error("Enter a valid number of players needed"); return; }
        if (form.end_time && form.end_time <= form.start_time) {
            toast.error("End time must be after start time");
            return;
        }
        try {
            await api.post("/finder/posts", {
                ...form,
                players_needed: pn,
                price_per_share: parseInt(form.price_per_share || 0),
            });
            toast.success("Game posted — other players are being notified");
            setShowForm(false);
            fetchPosts();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const join = async (id) => {
        try {
            const { data } = await api.post(`/finder/posts/${id}/join`);
            toast.success(`Joined! Contact: ${data.creator_phone || "shared via app"}`);
            fetchPosts();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const leave = async (id) => {
        if (!confirm("Leave this game?")) return;
        try {
            await api.post(`/finder/posts/${id}/leave`);
            toast.success("You left the game");
            fetchPosts();
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const remove = async (id) => {
        if (!confirm("Delete this post?")) return;
        await api.delete(`/finder/posts/${id}`);
        toast.success("Deleted");
        fetchPosts();
    };

    const createGroup = async (e) => {
        e.preventDefault();
        if ((groupForm.title || "").trim().length < 3) { toast.error("Group title must be at least 3 characters"); return; }
        try {
            const { data } = await api.post("/community/groups", groupForm);
            toast.success("Group created");
            setShowGroupForm(false);
            setGroupForm({ title: "", description: "", sport: "", image_url: "" });
            navigate(`/community/groups/${data.id}`);
        } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    };

    const joinGroup = async (gid) => {
        try {
            await api.post(`/community/groups/${gid}/join`);
            navigate(`/community/groups/${gid}`);
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const { mine, others } = useMemo(() => {
        const mine = posts.filter(p => p.user_id === user?.id);
        const others = posts.filter(p => p.user_id !== user?.id);
        return { mine, others };
    }, [posts, user?.id]);

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
            <Toaster theme="dark" richColors position="top-center" />
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-3xl font-bold uppercase text-white">Community</h1>
                    <p className="text-slate-400 text-sm mt-1">Find players. Make squads. Play more.</p>
                </div>
                {tab === "games" ? (
                    <button onClick={() => setShowForm(true)} data-testid="new-finder-post-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52] glow-green">
                        <Plus className="w-4 h-4" /> Post a Game
                    </button>
                ) : (
                    <button onClick={() => setShowGroupForm(true)} data-testid="new-group-btn"
                        className="inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded hover:bg-[#00cc52] glow-green">
                        <Plus className="w-4 h-4" /> New Group
                    </button>
                )}
            </div>

            <div className="mt-6 inline-flex p-1 bg-slate-900 border border-slate-800 rounded-lg">
                <button onClick={() => setTab("games")} data-testid="tab-games"
                    className={`px-4 h-9 rounded-md text-xs font-bold uppercase tracking-widest ${
                        tab === "games" ? "bg-[#00FF66] text-black" : "text-slate-400 hover:text-white"
                    }`}>Games</button>
                <button onClick={() => setTab("groups")} data-testid="tab-groups"
                    className={`px-4 h-9 rounded-md text-xs font-bold uppercase tracking-widest ${
                        tab === "groups" ? "bg-[#00FF66] text-black" : "text-slate-400 hover:text-white"
                    }`}>Groups</button>
            </div>

            {tab === "games" && (
                <>
                    <div className="flex gap-2 overflow-x-auto pb-2 mt-6 -mx-4 px-4 sm:mx-0 sm:px-0">
                        {["All", ...SPORTS].map(s => (
                            <button key={s} onClick={() => setSportFilter(s)}
                                data-testid={`finder-sport-${s.toLowerCase().replace(/\s+/g, '-')}`}
                                className={`px-4 h-9 rounded-full text-xs font-bold uppercase tracking-widest whitespace-nowrap border ${
                                    sportFilter === s ? "bg-[#00FF66] text-black border-[#00FF66]" : "bg-slate-900 text-slate-300 border-slate-800 hover:border-[#00FF66]/50"
                                }`}>{s}</button>
                        ))}
                    </div>
                    {loading ? (
                        <div className="text-center py-16 text-slate-500">Loading...</div>
                    ) : (
                        <>
                            {mine.length > 0 && (
                                <section className="mt-6" data-testid="my-games-section">
                                    <SectionHeader icon={Sparkles} title="Your Posted Games" subtitle={`${mine.length} active · visible to every player`} />
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {mine.map(p => <PostCard key={p.id} p={p} user={user} onJoin={join} onLeave={leave} onRemove={remove} mine onOpen={() => navigate(`/community/games/${p.id}`)} />)}
                                    </div>
                                </section>
                            )}
                            <section className="mt-8" data-testid="games-near-me-section">
                                <SectionHeader icon={MapPin} title="Games Near Me"
                                    subtitle={others.length > 0 ? `${others.length} game${others.length === 1 ? "" : "s"} from other players — tap Join to play` : "No open games from other players right now"}
                                    countBadge={others.length} />
                                {others.length === 0 ? (
                                    <div className="mt-3 bg-slate-900 border border-dashed border-slate-700 rounded-lg p-8 text-center">
                                        <p className="text-slate-400 text-sm">{sportFilter !== "All" ? `No ${sportFilter} games posted by other players yet.` : "Nobody else has posted a game yet. Post yours and let others join!"}</p>
                                    </div>
                                ) : (
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {others.map(p => <PostCard key={p.id} p={p} user={user} onJoin={join} onLeave={leave} onRemove={remove} onOpen={() => navigate(`/community/games/${p.id}`)} />)}
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </>
            )}

            {tab === "groups" && (
                <div className="mt-6">
                    {loadingGroups ? (
                        <div className="text-center py-16 text-slate-500">Loading groups...</div>
                    ) : groups.length === 0 ? (
                        <div className="bg-slate-900 border border-dashed border-slate-700 rounded-lg p-10 text-center">
                            <MessagesSquare className="w-10 h-10 mx-auto text-slate-600" />
                            <p className="text-slate-300 mt-3 font-bold uppercase tracking-widest text-sm">No groups yet</p>
                            <p className="text-slate-500 text-sm mt-1">Be the first — create a group for your sport, locality, or crew.</p>
                            <button onClick={() => setShowGroupForm(true)} data-testid="empty-create-group-btn"
                                className="mt-4 inline-flex items-center gap-2 px-4 h-10 bg-[#00FF66] text-black font-bold uppercase tracking-widest text-xs rounded">
                                <Plus className="w-4 h-4" /> Create Group
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {groups.map(g => (
                                <div key={g.id} className="bg-slate-900 border border-slate-800 rounded-lg p-4 flex items-center gap-3 hover:border-[#00FF66]/50 transition cursor-pointer card-hover"
                                    onClick={() => g.is_member ? navigate(`/community/groups/${g.id}`) : joinGroup(g.id)}
                                    data-testid={`group-${g.id}`}>
                                    {g.image_url ? (
                                        <img src={g.image_url} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-700 flex-shrink-0" />
                                    ) : (
                                        <div className="w-14 h-14 rounded-lg bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center flex-shrink-0">
                                            <Users className="w-6 h-6 text-[#00FF66]" />
                                        </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h3 className="font-display font-bold text-white uppercase truncate">{g.title}</h3>
                                            {g.sport && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">{g.sport}</span>}
                                            {g.is_member && <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/40">Member</span>}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-0.5">{g.members_count} member{g.members_count === 1 ? "" : "s"}</div>
                                        {g.last_message ? (
                                            <div className="text-xs text-slate-400 mt-1 truncate">
                                                <span className="text-slate-500">{g.last_message.user_name}:</span> {g.last_message.preview}
                                            </div>
                                        ) : (
                                            <div className="text-xs text-slate-600 mt-1 italic">No messages yet</div>
                                        )}
                                    </div>
                                    <ChevronRight className="w-5 h-5 text-slate-600 flex-shrink-0" />
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {showForm && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <form onSubmit={submit} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 relative max-h-[90vh] overflow-y-auto" data-testid="finder-form">
                        <button type="button" onClick={() => setShowForm(false)} className="absolute top-3 right-3 text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
                        <h3 className="font-display text-2xl font-bold uppercase text-white">Post a Game</h3>
                        <p className="text-xs text-slate-400 -mt-2">Other players will see your game on their Community page and can request to join.</p>
                        <Select label="Sport" value={form.sport} onChange={v => setForm({ ...form, sport: v })} options={SPORTS} testid="finder-sport-select" />
                        <Select label="Centre" value={form.centre_id} onChange={v => setForm({ ...form, centre_id: v })}
                            options={centres.map(c => ({ value: c.id, label: c.name }))} testid="finder-centre-select" />
                        <div className="grid grid-cols-1 gap-2">
                            <Input label="Date" type="date" value={form.post_date} onChange={v => setForm({ ...form, post_date: v })} testid="finder-date" />
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Start Time</label>
                                    <div className="mt-1">
                                        <TimePicker12 value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testid="finder-start-time" />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">End Time</label>
                                    <div className="mt-1">
                                        <TimePicker12 value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testid="finder-end-time" />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Players Needed</label>
                            <input type="number" min="1" max="99" step="1" value={form.players_needed}
                                onChange={(e) => setForm({ ...form, players_needed: e.target.value })} required
                                placeholder="e.g. 4" data-testid="finder-players"
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                            <p className="text-[11px] text-slate-500 mt-1">Type any number — how many players you're looking for.</p>
                        </div>
                        <Input label="Player Per Share (₹)" type="number" value={form.price_per_share} onChange={v => setForm({ ...form, price_per_share: v })} testid="finder-price" />
                        <p className="text-[11px] text-slate-500 -mt-1">Optional — what each joining player chips in toward the slot cost.</p>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Note (optional)</label>
                            <textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={2}
                                placeholder="e.g. Beginner-friendly, casual game" data-testid="finder-note"
                                className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <button type="submit" data-testid="submit-finder-btn"
                            className="w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] glow-green">Post Game</button>
                    </form>
                </div>
            )}

            {showGroupForm && (
                <div className="fixed inset-0 z-50 bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4">
                    <form onSubmit={createGroup} className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-3 relative" data-testid="new-group-form">
                        <button type="button" onClick={() => setShowGroupForm(false)} className="absolute top-3 right-3 text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
                        <h3 className="font-display text-2xl font-bold uppercase text-white">New Group</h3>
                        <p className="text-xs text-slate-400 -mt-2">Anyone can create a group. Members can chat and share posters &amp; games.</p>
                        <Input label="Title" value={groupForm.title} onChange={v => setGroupForm({ ...groupForm, title: v })} testid="group-title-input" />
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Description</label>
                            <textarea value={groupForm.description} onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })} rows={2}
                                data-testid="group-desc-input"
                                className="w-full mt-1 px-3 py-2 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Sport (optional)</label>
                            <select value={groupForm.sport} onChange={(e) => setGroupForm({ ...groupForm, sport: e.target.value })}
                                data-testid="group-sport-select"
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white">
                                <option value="">Any</option>
                                {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">Cover Image URL (optional)</label>
                            <input value={groupForm.image_url} onChange={(e) => setGroupForm({ ...groupForm, image_url: e.target.value })}
                                data-testid="group-image-input"
                                placeholder="https://…"
                                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
                        </div>
                        <button type="submit" data-testid="submit-group-btn"
                            className="w-full h-11 bg-[#00FF66] text-black font-bold uppercase tracking-widest rounded hover:bg-[#00cc52] glow-green">Create Group</button>
                    </form>
                </div>
            )}
        </div>
    );
}

function SectionHeader({ icon: Icon, title, subtitle, countBadge }) {
    return (
        <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#00FF66]/15 border border-[#00FF66]/40 flex items-center justify-center">
                    <Icon className="w-4 h-4 text-[#00FF66]" />
                </div>
                <div>
                    <h2 className="font-display text-xl font-bold uppercase text-white">{title}</h2>
                    <p className="text-xs text-slate-500">{subtitle}</p>
                </div>
            </div>
            {typeof countBadge === "number" && countBadge > 0 && (
                <span className="inline-flex items-center gap-2 px-3 h-8 rounded-full bg-[#00FF66]/15 border border-[#00FF66]/40 text-[#00FF66] text-xs font-bold uppercase tracking-widest">
                    {countBadge} live
                </span>
            )}
        </div>
    );
}

function PostCard({ p, user, onJoin, onLeave, onRemove, mine, onOpen }) {
    const joinersCount = (p.joiners || []).length;
    const filled = joinersCount + 1; // +1 = host counts as a player
    const spotsLeft = Math.max(0, p.players_needed - filled);
    const alreadyJoined = (p.joiners || []).some(j => j.user_id === user?.id);
    const full = spotsLeft === 0;
    const stop = (e) => e.stopPropagation();
    return (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 relative card-hover cursor-pointer hover:border-[#00FF66]/50 transition"
            data-testid={`finder-post-${p.id}`} onClick={onOpen}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-[#00FF66]/15 text-[#00FF66] border border-[#00FF66]/40">{p.sport}</span>
                        {mine && (<span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-pink-500/15 text-pink-300 border border-pink-500/40">Yours</span>)}
                        {full && !mine && (<span className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-slate-700 text-slate-300 border border-slate-600">Full</span>)}
                        {p.price_per_share > 0 && (
                            <span data-testid={`price-${p.id}`}
                                className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40">
                                ₹{p.price_per_share} / player
                            </span>
                        )}
                    </div>
                    <h3 className="font-display text-lg font-bold uppercase text-white mt-2 truncate">
                        {mine ? "You" : p.user_name}
                        <span className="text-slate-500 text-sm font-normal normal-case ml-2">looking for {p.players_needed}</span>
                    </h3>
                </div>
                <div className={`flex-shrink-0 text-right ${full ? "opacity-50" : ""}`} data-testid={`spots-left-${p.id}`}>
                    <div className="font-display text-2xl font-bold text-[#00FF66] leading-none">{spotsLeft}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-0.5">spots left</div>
                </div>
            </div>
            {p.note && <p className="text-sm text-slate-300 mt-3">{p.note}</p>}
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-400">
                <div className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-[#00FF66]" />{p.centre_name}</div>
                <div className="inline-flex items-center gap-1"><Calendar className="w-3 h-3 text-[#00FF66]" />{ddmmyyyy(p.post_date)}</div>
                <div className="inline-flex items-center gap-1"><Clock className="w-3 h-3 text-[#00FF66]" />{fmt12(p.start_time)}{p.end_time ? ` – ${fmt12(p.end_time)}` : ""}</div>
                <div className="inline-flex items-center gap-1"><Users className="w-3 h-3 text-[#00FF66]" />{filled}/{p.players_needed} joined</div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-800 flex items-center justify-between gap-2">
                <div className="flex -space-x-2">
                    {/* Host avatar first */}
                    <div title={`${p.user_name} (host)`}
                        className="w-7 h-7 rounded-full bg-amber-300 text-slate-950 font-display font-bold text-xs flex items-center justify-center border-2 border-slate-900">
                        {p.user_name?.charAt(0)}
                    </div>
                    {(p.joiners || []).slice(0, 4).map((j, i) => (<div key={i} title={j.name} className="w-7 h-7 rounded-full bg-[#00FF66] text-black font-display font-bold text-xs flex items-center justify-center border-2 border-slate-900">{j.name?.charAt(0)}</div>))}
                    {joinersCount === 0 && <span className="ml-2 text-xs text-slate-500">Be the first to join</span>}
                    {joinersCount > 4 && <span className="ml-3 text-xs text-slate-400">+{joinersCount - 4}</span>}
                </div>
                {mine ? (
                    <button onClick={(e) => { stop(e); onRemove(p.id); }} data-testid={`del-post-${p.id}`}
                        className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest text-red-400 border border-red-900/50 rounded hover:bg-red-950">
                        <Trash2 className="w-3 h-3" /> Delete
                    </button>
                ) : alreadyJoined ? (
                    <button onClick={(e) => { stop(e); onLeave(p.id); }} data-testid={`leave-${p.id}`}
                        className="inline-flex items-center gap-1 px-3 h-8 text-[10px] font-bold uppercase tracking-widest text-amber-400 border border-amber-700/60 rounded hover:bg-amber-950/30">
                        <Phone className="w-3 h-3" /> Leave game
                    </button>
                ) : (
                    <button onClick={(e) => { stop(e); onJoin(p.id); }} disabled={full}
                        data-testid={`join-${p.id}`}
                        className="px-4 h-8 bg-[#00FF66] text-black font-bold uppercase text-[10px] tracking-widest rounded hover:bg-[#00cc52] disabled:opacity-40 disabled:cursor-not-allowed">
                        {full ? "Full" : "Join"}
                    </button>
                )}
            </div>
        </div>
    );
}

function Input({ label, type = "text", value, onChange, testid, step, min, max }) {
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{label}</label>
            <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required data-testid={testid}
                step={step} min={min} max={max}
                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]" />
        </div>
    );
}

function Select({ label, value, onChange, options, testid }) {
    const opts = options.map(o => typeof o === "string" ? { value: o, label: o } : o);
    return (
        <div>
            <label className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400">{label}</label>
            <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}
                className="w-full mt-1 h-10 px-3 bg-slate-950 border border-slate-700 rounded text-white focus:outline-none focus:ring-2 focus:ring-[#00FF66]">
                {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
        </div>
    );
}
